const TOUCH_SLOP = 10;
const PINCH_TIMEOUT_MS = 250;

const tryPreventDefault = (e: any) => {
  // Only call preventDefault if the event is cancelable (i.e., not passive).
  if (e && typeof e.preventDefault === "function" && e.cancelable) {
    e.preventDefault();
  }
};

const SUPPRESS_SCROLL = (e: Event): DefaultState => {
  tryPreventDefault(e);
  return (this as unknown) as DefaultState;
};
interface CanvasDraw {
  props: any;
  coordSystem: any;
  lazy: any;
  points: any[];
  clampPointToDocument: (pt: any) => any;
  drawPoints: (args: any) => void;
  saveLine: () => void;
  setView?: (view: { x: number; y: number }) => void;
}

interface CoordSystem {
  x: number;
  y: number;
  scale: number;
  scaleAtClientPoint: (dScale: number, point: { clientX: number; clientY: number }) => void;
  setView: (view: { x: number; y: number }) => void;
  clientPointToViewPoint: (pt: { clientX: number; clientY: number }) => { x: number; y: number };
}

interface SyntheticTouch {
  clientX: number;
  clientY: number;
}

interface TouchMetrics {
  t1: SyntheticTouch;
  t2: SyntheticTouch;
  distance: number;
  centroid: SyntheticTouch;
}

export class DefaultState {
  handleMouseWheel = (e: any, canvasDraw: CanvasDraw): DefaultState | DisabledState => {
    const { disabled, enablePanAndZoom, mouseZoomFactor } = canvasDraw.props;
    if (disabled) {
      return new DisabledState();
    } else if (enablePanAndZoom && e.ctrlKey) {
      e.preventDefault();
      canvasDraw.coordSystem.scaleAtClientPoint(mouseZoomFactor * e.deltaY, clientPointFromEvent(e));
    }
    return this;
  };

  handleDrawStart = (e: any, canvasDraw: CanvasDraw): DefaultState | PanState | WaitForPinchState => {
    if (canvasDraw.props.disabled) {
      return new DisabledState();
    } else if (e.ctrlKey && canvasDraw.props.enablePanAndZoom) {
      return new PanState().handleDrawStart(e, canvasDraw);
    } else {
      return new WaitForPinchState().handleDrawStart(e, canvasDraw);
    }
  };

  handleDrawMove = (e: any, canvasDraw: CanvasDraw): DefaultState => {
    if (canvasDraw.props.disabled) {
      return new DisabledState();
    } else {
      const { x, y } = viewPointFromEvent(canvasDraw.coordSystem, e);
      canvasDraw.lazy.update({ x, y });
      return this;
    }
  };

  handleDrawEnd = (_e: any, canvasDraw: CanvasDraw): DefaultState | DisabledState => {
    return canvasDraw.props.disabled ? new DisabledState() : this;
  };
}

export class DisabledState {
  handleMouseWheel = (e: any, canvasDraw: CanvasDraw): DisabledState | DefaultState => {
    if (canvasDraw.props.disabled) {
      return this;
    } else {
      return new DefaultState().handleMouseWheel(e, canvasDraw);
    }
  };

  handleDrawStart = (e: any, canvasDraw: CanvasDraw): DisabledState | DefaultState => {
    if (canvasDraw.props.disabled) {
      return this;
    } else {
      return new DefaultState().handleDrawStart(e, canvasDraw);
    }
  };

  handleDrawMove = (e: any, canvasDraw: CanvasDraw): DisabledState | DefaultState => {
    if (canvasDraw.props.disabled) {
      return this;
    } else {
      return new DefaultState().handleDrawMove(e, canvasDraw);
    }
  };

  handleDrawEnd = (e: any, canvasDraw: CanvasDraw): DisabledState | DefaultState => {
    if (canvasDraw.props.disabled) {
      return this;
    } else {
      return new DefaultState().handleDrawEnd(e, canvasDraw);
    }
  };
}

export class PanState {
  dragStart!: { clientX: number; clientY: number };
  panStart!: { x: number; y: number };

  handleMouseWheel = SUPPRESS_SCROLL.bind(this);

  handleDrawStart = (e: any, canvasDraw: CanvasDraw): PanState => {
    e.preventDefault();

    this.dragStart = clientPointFromEvent(e);
    this.panStart = { x: canvasDraw.coordSystem.x, y: canvasDraw.coordSystem.y };

    return this;
  };

  handleDrawMove = (e: any, canvasDraw: CanvasDraw): PanState => {
    tryPreventDefault(e);

    const { clientX, clientY } = clientPointFromEvent(e);
    const dx = clientX - this.dragStart.clientX;
    const dy = clientY - this.dragStart.clientY;
    canvasDraw.coordSystem.setView({ x: this.panStart.x + dx, y: this.panStart.y + dy });

    return this;
  };

  handleDrawEnd = (): DefaultState => new DefaultState();
}

export class WaitForPinchState {
  startClientPoint: SyntheticTouch | null = null;
  startTimestamp: number;
  deferredPoints: SyntheticTouch[] = [];

  constructor() {
    this.startTimestamp = new Date().valueOf();
  }

  handleMouseWheel = SUPPRESS_SCROLL.bind(this);

  handleDrawStart = (e: any, canvasDraw: CanvasDraw): DrawingState | ScaleOrPanState | WaitForPinchState | DefaultState => {
    const { enablePanAndZoom } = canvasDraw.props;
    e.preventDefault();

    if (!e.touches || !e.touches.length || !enablePanAndZoom) {
      return new DrawingState().handleDrawStart(e, canvasDraw);
    }

    if (enablePanAndZoom && e.touches && e.touches.length >= 2) {
      return new ScaleOrPanState().handleDrawStart(e, canvasDraw);
    }

    return this.handleDrawMove(e, canvasDraw);
  };

  handleDrawMove = (e: any, canvasDraw: CanvasDraw): WaitForPinchState | DrawingState | DefaultState | ScaleOrPanState => {
    tryPreventDefault(e);

    if (e.touches && e.touches.length >= 2) {
      return new ScaleOrPanState().handleDrawStart(e, canvasDraw);
    }

    const clientPt = clientPointFromEvent(e);
    this.deferredPoints.push(clientPt);

    if (new Date().valueOf() - this.startTimestamp < PINCH_TIMEOUT_MS) {
      if (this.startClientPoint === null) {
        this.startClientPoint = clientPt;
      }

      const d =
        Math.abs(clientPt.clientX - this.startClientPoint.clientX) +
        Math.abs(clientPt.clientY - this.startClientPoint.clientY);

      if (d < TOUCH_SLOP) {
        return this;
      }
    }

    return this.issueDeferredPoints(canvasDraw);
  };

  handleDrawEnd = (e: any, canvasDraw: CanvasDraw): DefaultState | DrawingState => {
    return this.issueDeferredPoints(canvasDraw).handleDrawEnd(e, canvasDraw);
  };

  issueDeferredPoints = (canvasDraw: CanvasDraw): DrawingState => {
    let nextState = new DrawingState();
    for (let i = 0; i < this.deferredPoints.length; i++) {
      const deferredPt = this.deferredPoints[i];
      const syntheticEvt = new SyntheticEvent(deferredPt);
      const func = i === 0 ? nextState.handleDrawStart : nextState.handleDrawMove;
      nextState = func(syntheticEvt, canvasDraw);
    }
    return nextState;
  };
}

export class ScaleOrPanState {
  start!: TouchMetrics;
  panStart!: { x: number; y: number };
  scaleStart!: number;
  recentMetrics!: TouchMetrics;

  handleMouseWheel = SUPPRESS_SCROLL.bind(this);

  handleDrawStart = (e: any, canvasDraw: CanvasDraw): ScaleOrPanState | DefaultState => {
    e.preventDefault();
    if (!e.touches || e.touches.length < 2) {
      return new DefaultState();
    }
    this.start = this.getTouchMetrics(e);
    this.panStart = { x: canvasDraw.coordSystem.x, y: canvasDraw.coordSystem.y };
    this.scaleStart = canvasDraw.coordSystem.scale;
    return this;
  };

  handleDrawMove = (e: any, canvasDraw: CanvasDraw): TouchScaleState | TouchPanState | ScaleOrPanState | DefaultState => {
    tryPreventDefault(e);

    if (!e.touches || e.touches.length < 2) {
      return new DefaultState();
    }

    const { centroid, distance } = (this.recentMetrics = this.getTouchMetrics(e));

    const dd = Math.abs(distance - this.start.distance);
    if (dd >= TOUCH_SLOP) {
      return new TouchScaleState(this).handleDrawMove(e, canvasDraw);
    }

    const dx = centroid.clientX - this.start.centroid.clientX;
    const dy = centroid.clientY - this.start.centroid.clientY;
    const dc = Math.abs(dx) + Math.abs(dy);
    if (dc >= TOUCH_SLOP) {
      return new TouchPanState(this).handleDrawMove(e, canvasDraw);
    }

    return this;
  };

  handleDrawEnd = (): DefaultState => new DefaultState();

  getTouchMetrics = (e: any): TouchMetrics => {
    const { clientX: t1x, clientY: t1y } = clientPointFromEvent(e.touches[0]);
    const { clientX: t2x, clientY: t2y } = clientPointFromEvent(e.touches[1]);

    const dx = t2x - t1x;
    const dy = t2y - t1y;

    return {
      t1: { clientX: t1x, clientY: t1y },
      t2: { clientX: t2x, clientY: t2y },
      distance: Math.sqrt(dx * dx + dy * dy),
      centroid: { clientX: (t1x + t2x) / 2.0, clientY: (t1y + t2y) / 2.0 },
    };
  };
}

export class TouchPanState {
  scaleOrPanState: ScaleOrPanState;

  constructor(scaleOrPanState: ScaleOrPanState) {
    this.scaleOrPanState = scaleOrPanState;
  }

  handleMouseWheel = SUPPRESS_SCROLL.bind(this);
  handleDrawStart = (): TouchPanState => this;

  handleDrawMove = (e: any, canvasDraw: CanvasDraw): TouchPanState | DefaultState => {
    tryPreventDefault(e);

    if (!e.touches || e.touches.length < 2) {
      return new DefaultState();
    }

    const ref = this.scaleOrPanState;
    const { centroid } = (ref.recentMetrics = ref.getTouchMetrics(e));

    const dx = centroid.clientX - ref.start.centroid.clientX;
    const dy = centroid.clientY - ref.start.centroid.clientY;

    canvasDraw.setView?.({ x: ref.panStart.x + dx, y: ref.panStart.y + dy });

    return this;
  };

  handleDrawEnd = (): DefaultState => new DefaultState();
}

export class TouchScaleState {
  scaleOrPanState: ScaleOrPanState;

  constructor(scaleOrPanState: ScaleOrPanState) {
    this.scaleOrPanState = scaleOrPanState;
  }

  handleMouseWheel = SUPPRESS_SCROLL.bind(this);
  handleDrawStart = (): TouchScaleState => this;

  handleDrawMove = (e: any, canvasDraw: CanvasDraw): TouchScaleState | DefaultState => {
    tryPreventDefault(e);

    if (!e.touches || e.touches.length < 2) {
      return new DefaultState();
    }

    const ref = this.scaleOrPanState;
    const { centroid, distance } = (ref.recentMetrics = ref.getTouchMetrics(e));

    const targetScale = ref.scaleStart * (distance / ref.start.distance);
    const dScale = targetScale - canvasDraw.coordSystem.scale;
    canvasDraw.coordSystem.scaleAtClientPoint(dScale, centroid);

    return this;
  };

  handleDrawEnd = (): DefaultState => new DefaultState();
}

export class DrawingState {
  isDrawing: boolean = false;

  handleMouseWheel = SUPPRESS_SCROLL.bind(this);

  handleDrawStart = (e: any, canvasDraw: CanvasDraw): DrawingState => {
    tryPreventDefault(e);


    if (e.touches && e.touches.length) {
      const { x, y } = viewPointFromEvent(canvasDraw.coordSystem, e);
      canvasDraw.lazy.update({ x, y }, { both: true });
    }

    return this.handleDrawMove(e, canvasDraw);
  };

  handleDrawMove = (e: any, canvasDraw: CanvasDraw): DrawingState => {
    tryPreventDefault(e);


    const { x, y } = viewPointFromEvent(canvasDraw.coordSystem, e);
    canvasDraw.lazy.update({ x, y });
    const isDisabled = !canvasDraw.lazy.isEnabled();

    if (!this.isDrawing || isDisabled) {
      canvasDraw.points.push(canvasDraw.clampPointToDocument(canvasDraw.lazy.brush.toObject()));
      this.isDrawing = true;
    }

    canvasDraw.points.push(canvasDraw.clampPointToDocument(canvasDraw.lazy.brush.toObject()));

    canvasDraw.drawPoints({
      points: canvasDraw.points,
      brushColor: canvasDraw.props.brushColor,
      brushRadius: canvasDraw.props.brushRadius,
    });

    return this;
  };

  handleDrawEnd = (e: any, canvasDraw: CanvasDraw): DefaultState => {
    e.preventDefault();

    this.handleDrawMove(e, canvasDraw);
    canvasDraw.saveLine();

    return new DefaultState();
  };
}

export class SyntheticEvent {
  clientX: number;
  clientY: number;
  touches: SyntheticTouch[];

  constructor({ clientX, clientY }: SyntheticTouch) {
    this.clientX = clientX;
    this.clientY = clientY;
    this.touches = [{ clientX, clientY }];
  }

  preventDefault = (): void => { };
}

export function clientPointFromEvent(e: any): SyntheticTouch {
  let clientX = e.clientX;
  let clientY = e.clientY;

  if (e.changedTouches && e.changedTouches.length > 0) {
    clientX = e.changedTouches[0].clientX;
    clientY = e.changedTouches[0].clientY;
  }

  return { clientX, clientY };
}

export function viewPointFromEvent(coordSystem: CoordSystem, e: any): { x: number; y: number } {
  return coordSystem.clientPointToViewPoint(clientPointFromEvent(e));
}